import type {ResponseCookies} from 'next/dist/compiled/@edge-runtime/cookies';

/**
 * The cookie manager for both server and client components.
 *
 * On the client components, the `Cookie Store API` will be used
 * for managing the cookies if the user agent supports it.
 */
export default class Cookie {
  ONE_DAY_TIMESTAMP = 864e2;
  ONE_WEEK_TIMESTAMP = 6048e5;
  ONE_MONTH_TIMESTAMP = 2592e6;
  ONE_YEAR_TIMESTAMP = 31536e6;

  constructor(private cookieStore?: ResponseCookies | CookieStore) {}

  /** Sets the cookie store. */
  async #setCookieStore() {
    if (!this.cookieStore)
      if (typeof window === 'undefined') {
        this.cookieStore = await (await import('next/headers')).cookies();
      } else if ('cookieStore' in window) {
        this.cookieStore = window.cookieStore;
      }
  }

  /** Sets the cookie value for the provided cookie name. */
  async set(options: CookieInit) {
    await this.#setCookieStore();

    if (this.cookieStore) {
      if (typeof window === 'undefined') {
        // `ResponseCookies` expects `expires` property as a `Date` object
        const _options: any = {...options};
        if (typeof _options.expires === 'number')
          _options.expires = new Date(_options.expires);

        (this.cookieStore as ResponseCookies).set(_options);
      } else {
        await (this.cookieStore as CookieStore).set(options);
      }
    } else {
      const {name, value, ..._options} = options;
      const mergedOptions = Object.entries(_options).reduce(
        (result, [key, value]) => `${result}; ${key}=${value}`,
        ''
      );
      document.cookie = `${name}=${encodeURIComponent(value)}${mergedOptions}`;
    }
  }

  /** Returns the cookie value from the provided cookie name. */
  async get(name: string) {
    await this.#setCookieStore();

    if (this.cookieStore) {
      const value = (await this.cookieStore.get(name))?.value;
      if (value) {
        return decodeURIComponent(value);
      }
    } else {
      for (const cookie of document.cookie.split('; ')) {
        const parts = cookie.split('=');
        if (parts[0] === name) {
          return decodeURIComponent(parts[1]);
        }
      }
    }
  }

  /** Deletes the provided cookie name from the storage. */
  async delete(name: string, path?: '/') {
    await this.#setCookieStore();

    if (this.cookieStore) {
      await this.cookieStore.delete(name);
    } else {
      await this.set({
        name,
        value: '',
        expires: 0,
        path: path || ''
      });
    }
  }
}
